import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import ConfirmModal from '../components/ConfirmModal';
import UserStatsModal from '../components/UserStatsModal';
import {
  HiOutlineUsers,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineSearch,
  HiOutlineChartBar,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

function AdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_active: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    user: null,
  });
  const [statsModal, setStatsModal] = useState({
    isOpen: false,
    user: null,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      // Filter out admin users from the list
      const nonAdminUsers = response.data.filter(user => !user.is_admin);
      setUsers(nonAdminUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || t('users.errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        is_active: user.is_active,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.email) {
      toast.error(t('users.fillRequired'));
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error(t('users.passwordRequired'));
      return;
    }

    try {
      if (editingUser) {
        const updateData = {
          username: formData.username,
          email: formData.email,
          is_active: formData.is_active,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await usersAPI.update(editingUser.id, updateData);
        toast.success(t('users.userUpdated'));
      } else {
        await usersAPI.create(formData);
        toast.success(t('users.userCreated'));
      }
      closeModal();
      loadUsers();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = (user) => {
    if (user.is_admin) {
      toast.error(t('users.cannotDeleteAdmin'));
      return;
    }
    setConfirmModal({ isOpen: true, user });
  };

  const deleteUser = async () => {
    if (!confirmModal.user) return;
    
    try {
      await usersAPI.delete(confirmModal.user.id);
      toast.success(t('users.userDeleted'));
      setConfirmModal({ isOpen: false, user: null });
      loadUsers();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || t('users.errorDeletingUser'));
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <h1>{t('users.manageUsersTitle')}</h1>
        <p>{t('users.manageUsersSubtitle')}</p>
      </div>

      <div className="card users-card">
        <div className="card-header">
          <h2>{t('users.usersCount', { count: users.length })}</h2>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <HiOutlinePlus />
            {t('users.newUser')}
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '0 24px 16px' }}>
          <div className="search-container" style={{ marginBottom: 0, maxWidth: '300px', height: '38px' }}>
            <HiOutlineSearch className="search-icon" style={{ width: '18px', height: '18px' }} />
            <input
              type="text"
              className="search-input"
              placeholder={t('users.searchUsers')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ height: '38px', minHeight: '38px', padding: '0 40px 0 40px' }}
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                style={{ width: '20px', height: '20px', right: '10px' }}
              >
                <HiOutlineX size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="users-list-scrollable">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('users.user')}</th>
                    <th>{t('common.email')}</th>
                    <th>{t('common.status')}</th>
                    <th style={{ width: '200px' }}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: '600',
                              fontSize: '14px',
                            }}
                          >
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: '500' }}>{user.username}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge ${
                          !user.email_verified
                            ? 'badge-warning'
                            : user.is_active
                              ? 'badge-success'
                              : 'badge-error'
                        }`}>
                          {!user.email_verified
                            ? t('common.pending')
                            : user.is_active
                              ? t('common.active')
                              : t('common.inactive')
                          }
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-icon"
                            onClick={() => setStatsModal({ isOpen: true, user })}
                            title={t('users.statistics')}
                          >
                            <HiOutlineChartBar />
                          </button>
                          <button
                            className="btn btn-icon"
                            onClick={() => openModal(user)}
                            title={t('common.edit')}
                          >
                            <HiOutlinePencil />
                          </button>
                          {!user.is_admin && (
                            <button
                              className="btn btn-icon"
                              onClick={() => handleDeleteClick(user)}
                              title={t('common.delete')}
                              style={{ color: 'var(--error)' }}
                            >
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <HiOutlineUsers size={32} />
              </div>
              <h3>{searchQuery ? t('users.noUsersFound') : t('users.noUsers')}</h3>
              <p>{searchQuery ? t('users.tryDifferentSearch') : t('users.createFirstUser')}</p>
            </div>
          )}
        </div>
      </div>

      {/* User Stats Modal */}
      <UserStatsModal
        isOpen={statsModal.isOpen}
        user={statsModal.user}
        onClose={() => setStatsModal({ isOpen: false, user: null })}
      />

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={t('users.deleteUser')}
        message={t('users.confirmDeleteUser', { username: confirmModal.user?.username })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={deleteUser}
        onCancel={() => setConfirmModal({ isOpen: false, user: null })}
        type="danger"
      />

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? t('users.editUser') : t('users.newUser')}</h2>
              <button className="modal-close" onClick={closeModal}>
                <HiOutlineX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">{t('users.user')} *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    placeholder={t('users.enterUsername')}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('common.email')} *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder={t('users.enterEmail')}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('common.password')} {editingUser ? t('users.passwordKeepBlank') : '*'}
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder={t('users.enterPassword')}
                  />
                </div>

                {editingUser && !editingUser.is_admin && (
                  <div className="form-group">
                    <label
                      className="checkbox-container"
                      onClick={() =>
                        setFormData({ ...formData, is_active: !formData.is_active })
                      }
                    >
                      <div className={`checkbox ${formData.is_active ? 'checked' : ''}`}>
                        {formData.is_active && <HiOutlineCheck size={14} />}
                      </div>
                      <span>{t('users.userActive')}</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? t('common.save') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
